import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  Link,
} from '@mui/material';
import BlockData from '../../../../models/BlockData';
import LinkData from '../../../../models/LinkData';
import Utils from '../../../../utils/Utils';
import BlockCarouselItemData from '../../../Blocks/BlockCarousel/BlockCarouselItemData';
import CloseButton from '../../../Buttons/Close';
import CroppedImage from '../../../CroppedImage';
import BlockComponentImage from '../../../../models/BlockComponentValues/BlockComponentImage';
import BasicImageSettingsModal from '../../ImageModal/BasicImageSettingsModal';
import EditableText from '../../../Form/EditableText';
import ImageSettingsModal from '../../ImageSettingsModal/ImageSettingsModal';
import { BlockHeight } from '../../../../models/BlockSettings';

interface CarouselItemProps {
  item: BlockCarouselItemData;
  blockData: BlockData;
  blockHeight: BlockHeight;
  isTemplate: boolean;
  isEditing: boolean;
  onRemoveBlock: () => void;
  onChangeImage: (image: BlockComponentImage) => void;
  onChangeLink: (link: LinkData) => void;
  onChangeAlt: (alt: string) => void;
  onChangeText: (text: string) => void;
  onRemoveText: () => void;
  hasFixedAspectRatio?: boolean;
}

function CarouselItem({
  item,
  blockData,
  isTemplate,
  blockHeight,
  isEditing,
  onRemoveBlock,
  onChangeImage,
  onChangeLink,
  onChangeAlt,
  onChangeText,
  onRemoveText,
  hasFixedAspectRatio,
}: CarouselItemProps) {
  // text settings
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [imageText, setImageText] = useState<string | null>(item.text?.value);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const openModal = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    setIsLoading(true);

    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const textValue = item.text?.value ? item.text?.value : '';
    setImageText(textValue);
  }, [item.text]);

  // Calculate appropriate container height for fixed aspect ratio
  const containerStyles = useMemo(() => {
    if (!hasFixedAspectRatio) {
      return {
        gridMaxHeight: '355px',
        boxMaxHeight: '350px',
        boxWidth: 'auto',
      };
    }

    // For fixed aspect ratio, remove height restrictions and let aspect ratio control height
    return {
      gridMaxHeight: 'none',
      boxMaxHeight: 'none',
      boxWidth: '100%',
    };
  }, [hasFixedAspectRatio]);

  return (
    <>
      <div className="d-flex align-items-center">
        <CloseButton updateHandler={onRemoveBlock} />
        <Grid item xs={12} sx={[Utils.hoverBorder(isEditing), { maxHeight: `${containerStyles.gridMaxHeight} !important` }]}>
          <Box
            className="innerBoxLogo"
            sx={{
              maxHeight: containerStyles.boxMaxHeight,
              width: containerStyles.boxWidth,
              margin: '2px',
              overflow: 'hidden',
              // set for text
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {(!isLoading) && (
              <Grid
                item
                sx={{
                  zIndex: 1,
                  position: 'absolute',
                  padding: '12px',
                  fontSize: '2em',
                  display: 'flex',
                }}
              >
                <EditableText
                  open={isOpen}
                  text={imageText}
                  textColor={item.text?.settings?.textColor}
                  isEditable={isEditing}
                  onChangeModalOpen={(open) => setIsOpen(open)}
                  initialText="スライダーテキスト"
                  updateHandler={(text) => {
                    if (text === '<p><br></p>' || !text) {
                      setImageText(null);
                      onChangeText(null);
                      return;
                    }

                    setImageText(text);
                    onChangeText(text);
                  }}
                />
              </Grid>
            )}

            {hasFixedAspectRatio && (
              <ImageSettingsModal
                image={item?.image}
                blockHeight={blockHeight}
                onConfirm={(img) => {
                  onChangeImage(img);
                }}
                disabled={false}
                isEditing
                noRadiusControls
                useFixedAspectRatio
                noAspectRatioControls
              >
                {isEditing ? (
                  <CroppedImage
                    image={item?.image}
                    blockHeight={blockHeight}
                    settings={item?.image?.settings}
                    objectFitStyle="contain"
                    useFixedAspectRatio
                  />
                ) : (
                  <Link
                    style={{ cursor: item?.link?.linkData?.url ? 'pointer' : 'default' }}
                    target={item?.link?.linkData?.openType}
                    href={item?.link?.linkData?.url || undefined}
                    rel="noreferrer"
                  >
                    <CroppedImage
                      image={item?.image}
                      blockHeight={blockHeight}
                      settings={item?.image?.settings}
                      objectFitStyle="contain"
                      useFixedAspectRatio
                    />
                  </Link>
                )}
              </ImageSettingsModal>
            )}

            {!hasFixedAspectRatio && (
              <BasicImageSettingsModal
                image={item?.image}
                onChange={(img) => {
                  onChangeImage(img);
                }}
                disabled={false}
                isEditing
              >
                {isEditing ? (
                  <CroppedImage
                    image={item?.image}
                    settings={item?.image?.settings}
                    blockHeight={blockHeight}
                    objectFitStyle="fill"
                  />
                ) : (
                  <Link
                    style={{ cursor: item?.link?.linkData?.url ? 'pointer' : 'default' }}
                    target={item?.link?.linkData?.openType}
                    href={item?.link?.linkData?.url || undefined}
                    rel="noreferrer"
                  >
                    <CroppedImage
                      image={item?.image}
                      settings={item?.image?.settings}
                      blockHeight={blockHeight}
                      objectFitStyle="fill"
                    />
                  </Link>
                )}
              </BasicImageSettingsModal>
            )}
          </Box>
        </Grid>
      </div>

      <Button
        variant="outlined"
        onClick={openModal}
        sx={{
          marginTop: '10px',
        }}
      >
        テキスト編集
      </Button>
    </>
  );
}

export default CarouselItem;
